import React, { useState, useEffect, ChangeEvent, KeyboardEvent, useRef, ReactHTMLElement } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useChat } from './aiservice/aiContext';
import { apiCall, updateUrlParameter } from './aiservice/aiservice';
import { aiServiceBaseUrl } from './aiComponent';
import { sendEvent } from '../modules/helpers/Analytics';

type BreadCrumbType = {
  cssClass: string;
  documentMetaType: string;
  menuTagText: string;
  target: string;
  text: string;
  title: string;
  url: string;
};

export type AutocompleteResponseType = {
  breadcrumbs: Array<BreadCrumbType>;
  changed: string;
  characterSection: string;
  contentReference: string;
  documentType: string;
  excerpt: string;
  subTitle: string;
  summary: string;
  title: string;
  url: string;
  urlWithTracking: string;
};

interface AutocompleteComponentProps {
    minChars?: number;
    endpoint: string;
    placeholder: string;
    aiVisibility: boolean;
    name: string;
    ainame: string;
    empty: string;
    queryvalue: string;
}

const AutocompleteComponent: React.FC<AutocompleteComponentProps> = ({ minChars = 2, endpoint, placeholder, name, empty, aiVisibility, ainame, queryvalue }) => {
    const [queryValue, setQueryValue] = useState<string>(queryvalue);
    const [query, setQuery] = useState<string>('');
    const [suggestions, setSuggestions] = useState<AutocompleteResponseType[]>([]);
    const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [userHasAiAccess, setUserHasAiAccess] = useState<boolean>(false);
    const [error, setError] = useState<string>();
    const [activeSuggestionIndex, setActiveSuggestionIndex] = useState<number>(-1);
    const isSelectingSuggestion = useRef(false);

    const fetchSuggestions = async () => {
        if (query.length >= minChars) {
            setIsLoading(true);
            try {
                const response = await fetch(`${endpoint}?query=${query}`);
                if (response.ok) {
                    const data: AutocompleteResponseType[] = await response.json();
                    setSuggestions(data);
                    setIsLoading(false);
                    setShowSuggestions(true);
                } else {
                    if (typeof response.statusText === "string") {
                        setError(response.statusText);
                    } else {
                        setError("Server svarade med ett fel. Kontakta kundtjänst.");
                    }
                    setIsLoading(false);
                    console.error('Error fetching autocomplete suggestions:', response.statusText);
                }
            } catch (error) {
                if (typeof error === "string") {
                    setError(error);
                } else {
                    setError("Nätverksfel / Server svarar ej");
                }
                setIsLoading(false);
                console.error('Error fetching autocomplete suggestions:', error);
            }
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }
    };

    const fetchSuggestionDebounce = useDebouncedCallback(() => {
        fetchSuggestions();
    }, 1000);

    useEffect(() => {
        if (!!error) {
            setTimeout(() => {
                setError('');
            }, 5000);
        }
    }, [error]);

    useEffect(() => {

        apiCall(aiServiceBaseUrl + "/hasaccess")
            .then((data: boolean) => {
                if (!!data) {
                    setUserHasAiAccess(data);
                }
            })
            .catch(error => {
                console.error(error);
                setUserHasAiAccess(false);
            });

    }, []);

    useEffect(() => {
        if (!showSuggestions) {
            setActiveSuggestionIndex(-1);
        }
    }, [showSuggestions]);

    useEffect(() => {
        if (!isSelectingSuggestion.current) {
            fetchSuggestionDebounce();
        } else {
            setIsLoading(false);
            isSelectingSuggestion.current = false;
        }
    }, [query, minChars, endpoint]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        setQuery(e.target.value);
        setQueryValue(e.target.value);
    };

    const handleSuggestionClick = (suggestion: AutocompleteResponseType) => {
        isSelectingSuggestion.current = true;
        setQuery(suggestion.title);
        setQueryValue(suggestion.title);
        setShowSuggestions(false);
        sendEvent('documentViewFromAutocomplete', undefined, { pageTitle: suggestion.title, documentType: suggestion.documentType });
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        switch (e.keyCode) {
            case 13: // Enter
                if (showSuggestions && activeSuggestionIndex >= 0) {
                    e.preventDefault();
                    isSelectingSuggestion.current = true;
                    setQuery(suggestions[activeSuggestionIndex].title);
                    setQueryValue(suggestions[activeSuggestionIndex].title);
                    setShowSuggestions(false);
                    setActiveSuggestionIndex(-1);
                    if (!!suggestions[activeSuggestionIndex]?.url) {
                        window.location.href = suggestions[activeSuggestionIndex].url;
                    }
                }
                break;
            case 38: // Up arrow
                e.preventDefault();
                setActiveSuggestionIndex((prevIndex) =>
                    prevIndex <= 0 ? suggestions.length - 1 : prevIndex - 1
                );
                break;
            case 40: // Down arrow
                e.preventDefault();
                setActiveSuggestionIndex((prevIndex) =>
                    prevIndex >= suggestions.length - 1 ? 0 : prevIndex + 1
                );
                break;
            default:
                break;
        }
    };

    function generateItemSubHeader(suggestion: AutocompleteResponseType): JSX.Element {
        let child = <>{suggestion.subTitle ? suggestion.subTitle : suggestion.documentType}</>;

        return <span className='subTitle'>{child}</span>;
    }

    function doAiSearch(e: React.MouseEvent) {
        e.preventDefault();
        updateUrlParameter('aiquery', query);
        location.reload();
    }

    function aiSection(): JSX.Element | null {
        if (!aiVisibility || !userHasAiAccess) {
            return null;
        }
        return (
            <div className="aiSearchField">
                <span>Testa att fråga FAR AI</span>
                <button tabIndex={0} className="genericBtn primary" id="aiQuickSearchBtn" onClick={doAiSearch}>{ainame}</button>
            </div>
        );
    }

    return (
        <>
            <input
                type="text"
                id="searchfield"
                value={queryValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                aria-label="Search"
                name={name}
                placeholder={placeholder}
                className="searchfield__input searchfield__input--small"
                required
                autoComplete="off"
            />
            <button type="submit" id="searchfieldbutton" className="searchfield__button searchfield__button">
                Sök
            </button>

      {(showSuggestions || !!error) && <>
        {isLoading ? <>
            <div className="infoWrapper">
                <div className="blocker__loader"/>
            </div>
          </>
          : showSuggestions && suggestions.length === 0 && query?.length > 0 ? <>
          <div className="infoWrapper">
            <div className='info'>{empty}</div>
            {aiSection()}
          </div>
          </>:<>
                  {!!error ? 
                  <div className="infoWrapper">
                    <span className='error'>Något gick fel!</span>
                    <small>{error}</small>
                  </div> : <>
                      <div className={`autocomplete ${showSuggestions ? 'open' : ''}`}>
                        <ul className="autocomplete__list">
                          {suggestions.map((suggestion, i) => (
                            
                            <li
                              key={i}
                              className={`autocomplete__item ${i === activeSuggestionIndex ? 'active' : ''}`}
                              onClick={() => handleSuggestionClick(suggestion)}
                            >
                              <a href={suggestion.url} target="_self" tabIndex={0}>
                                  {!!suggestion.breadcrumbs &&
                                  <div className="qs_breadCrumb">
                                    {generateItemSubHeader(suggestion)}
                                  </div>}
                                  <span className="suggestionTitle">{suggestion.title}</span>
                              </a>
                            </li>
                          ))}
                        </ul>
                        {aiSection()}
                      </div>
                  </>}
                </>
              }
            <>
        </>
        </>
      }
    </>
  );
};

export default AutocompleteComponent;
